import { ChangeDetectorRef, Component, DOCUMENT, DestroyRef, OnInit, computed, effect, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationStart, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Popover, PopoverModule } from 'primeng/popover';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { Menu, NavService } from '../../../core/services/common/nav.service';
import { FullscreenDirective } from '../../../shared/directives/fullscreen.directive';
import { AuthService } from '../../../features/authentication/data-access/auth.service';
import { OrganizationService } from '../../../features/organizations/pages/organization-list/data-access/organization.service';
import { OrganizationLookupDto } from '../../../features/organizations/pages/organization-list/models/organization.model';
import { LayoutService } from '../../../core/services/common/layout.service';
import { ThemeSettingsService } from '../../../core/services/common/theme-settings.service';

/** The bar across the top of every signed-in page: menu toggle, logo, search, theme, full screen, account. */
@Component({
  selector: 'app-header',
  templateUrl: './header.html',
  styleUrls: ['./header.scss'],
  imports: [RouterLink, FormsModule, ButtonModule, DialogModule, PopoverModule, FullscreenDirective],
})
export class Header implements OnInit {
  readonly layoutService = inject(LayoutService);
  /** The top bar logo follows the Settings panel. */
  readonly themeSettings = inject(ThemeSettingsService);
  public menuItems!: Menu[];
  public NavServices = inject(NavService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly doc = inject(DOCUMENT);
  readonly currentUser = this.authService.currentUserSignal;
  private readonly account = viewChild<Popover>('account');

  /** The search dialog is open. */
  readonly searchOpen = signal(false);

  /** The language in use, for the flag in the top bar (the same setting as the Settings panel's Language). */
  readonly currentLanguage = computed(
    () => this.themeSettings.languages.find((language) => language.code === this.themeSettings.language()) ?? this.themeSettings.languages[0],
  );

  /** A language was picked in the top bar menu: apply it (direction, lang, RTL font) and close the menu. */
  chooseLanguage(code: 'en' | 'ar', menu: Popover): void {
    this.themeSettings.setLanguage(code);
    menu.hide();
  }

  constructor() {
    // The account panel is appended to <body> and the bar is fixed, so PrimeNG's scroll handling never
    // sees the page move: close it on scroll, on navigation and when the session ends.
    const close = (event?: Event) => {
      const target = event?.target;
      if (target instanceof Element && target.closest('.ui-account-panel')) {
        return;
      }
      this.closePanels();
    };
    this.doc.addEventListener('scroll', close, true);
    this.destroyRef.onDestroy(() => {
      this.doc.removeEventListener('scroll', close, true);
      this.closePanels();
      this.doc.querySelectorAll('.ui-account-panel').forEach((el) => el.remove());
    });
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.closePanels());
    effect(() => {
      if (!this.currentUser()) {
        this.closePanels();
      }
    });
  }

  private closePanels(): void {
    const account = this.account();
    if (account?.overlayVisible) {
      account.hide();
    }
  }

  /**
   * PrimeNG lines the panel's right edge up with the button's, which leaves the pointer jammed against
   * the rounded corner. The panel is moved out until the pointer has room beside the corner (kept inside
   * the screen), and the pointer is put under the middle of the button.
   */
  pointAt(button: HTMLElement): void {
    const panel = this.doc.querySelector<HTMLElement>('.p-popover.ui-account-panel');
    if (!panel) {
      return;
    }
    const r = button.getBoundingClientRect();
    const centre = r.left + r.width / 2 + window.scrollX;
    const width = panel.offsetWidth;
    const screen = this.doc.documentElement.clientWidth;
    const gap = 12; // from the screen's edge
    const reach = 36; // from the pointer to the panel's corner edge
    const left = Math.max(gap, Math.min(centre + reach - width, screen - gap - width));
    panel.style.left = `${left}px`;
    panel.style.setProperty('--ui-arrow-x', `${centre - left}px`);
  }

  public items: Menu[] = []; // Your full menu data (source)
  public text = '';
  public SearchResultEmpty = false;

  // The search box finds two things: pages (the left-hand menu, by title) and organizations (by
  // name or code, from the server).
  private readonly organizationApi = inject(OrganizationService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  public organizationResults: OrganizationLookupDto[] = [];
  public organizationsLoading = false;
  private readonly organizationSearch$ = new Subject<string>();
  private readonly organizationSearchSubscription = this.organizationSearch$
    .pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((term) => {
        if (term.length < 2) return of([] as readonly OrganizationLookupDto[]);
        this.organizationsLoading = true;
        return this.organizationApi.autocomplete(term, 8).pipe(catchError(() => of([] as readonly OrganizationLookupDto[])));
      }),
      takeUntilDestroyed(this.destroyRef),
    )
    .subscribe((results) => {
      this.organizationsLoading = false;
      this.organizationResults = [...results];
      this.SearchResultEmpty = this.menuItems.length === 0 && results.length === 0 && this.text.trim().length > 0;
      // The results arrive outside any event, so the view is told to refresh (zoneless app).
      this.changeDetector.markForCheck();
    });

  Search(searchText: string) {
    const query = (searchText ?? '').toLowerCase().trim();
    if (!query) {
      this.menuItems = [];
      this.organizationResults = [];
      this.SearchResultEmpty = false;
      this.organizationSearch$.next('');
      return;
    }

    // Pages: every menu entry with a route whose title contains the text (groups are skipped).
    const results: Menu[] = [];
    const walk = (nodes: Menu[] | undefined, trail: string[]) => {
      for (const node of nodes ?? []) {
        const path = [...trail, node.title ?? ''];
        if (node.path && node.title?.toLowerCase().includes(query)) results.push({ ...node, title: path.join(' › ') });
        walk(node.children, path);
        walk(node.children2, path);
      }
    };
    walk(this.items, []);

    this.menuItems = results.slice(0, 8);
    this.SearchResultEmpty = results.length === 0 && this.organizationResults.length === 0;
    this.organizationSearch$.next(query);
  }

  /** A result was chosen: go there and close the search box. */
  openSearchResult(path: string | null | undefined): void {
    if (!path) return;
    this.closeSearch();
    void this.router.navigateByUrl(path);
  }

  /** The organization page: the Organization List filtered to that code. */
  openOrganization(organization: OrganizationLookupDto): void {
    this.openSearchResult('/organizations/list?search=' + encodeURIComponent(organization.code));
  }

  openSearch(): void {
    this.searchOpen.set(true);
  }

  closeSearch(): void {
    this.searchOpen.set(false);
    this.clearSearch();
  }

  //  Used to clear previous search result
  clearSearch() {
    this.text = '';
    this.menuItems = [];
    this.organizationResults = [];
    this.SearchResultEmpty = false;
  }

  isFullscreen = false;
  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
  }

  ngOnInit(): void {
    this.NavServices.items$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((menuItems: Menu[]) => {
      this.items = menuItems;
    });
  }

  readonly displayName = computed(() => {
    const email = this.currentUser()?.email?.trim();
    if (email) {
      const localPart = email.split('@')[0];
      const formatted = localPart
        .replace(/[._-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      if (formatted) {
        return formatted
          .split(' ')
          .filter(Boolean)
          .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
          .join(' ');
      }
    }

    return this.currentUser()?.displayName?.trim() || 'User';
  });

  readonly initials = computed(() =>
    this.displayName()
      .trim()
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join(''),
  );

  readonly primaryRoles = computed(() => {
    const roles = this.currentUser()?.roles ?? [];
    const formatted = roles
      .map((role) =>
        role
          .trim()
          .replace(/[_-]+/g, ' ')
          .replace(/\s+/g, ' ')
          .split(' ')
          .filter(Boolean)
          .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
          .join(' '),
      )
      .filter(Boolean);
    return formatted.length > 0 ? formatted : ['User'];
  });

  /** True while the sign-out request is in flight — a second click must not send it twice. */
  private signingOut = false;

  logout(): void {
    if (this.signingOut) {
      return;
    }
    this.signingOut = true;
    this.closePanels();
    // Revokes the refresh token on the server, then clears the local session.
    this.authService.signOut().subscribe(() => {
      this.signingOut = false;
      // replaceUrl: the protected page is dropped from history, so Back cannot return to it.
      void this.router.navigate(['/auth/login'], { replaceUrl: true });
    });
  }
}
